import React, { useState, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Reusable CoinToss component for any sport that requires a toss
 *
 * Props:
 * - isOpen: boolean - Whether the modal is open
 * - onClose: function - Called when modal is closed
 * - onTossComplete: function({ winner: string, choice: string }) - Called when toss is complete
 * - teamA: { id: string, name: string } - First team
 * - teamB: { id: string, name: string } - Second team
 * - choices: string[] - Array of choices after winning toss (e.g., ['Bat First', 'Field First'])
 * - title: string - Optional title (default: 'Coin Toss')
 */
const CoinToss = ({
  isOpen,
  onClose,
  onTossComplete,
  teamA,
  teamB,
  choices = ['Bat First', 'Field First'],
  title = 'Coin Toss'
}) => {
  const { theme } = useTheme();
  const [stage, setStage] = useState('spinning'); // 'spinning', 'result', 'skip-select', 'choice'
  const [isSpinning, setIsSpinning] = useState(true);
  const [coinFace, setCoinFace] = useState('heads'); // Current display during spin
  const [tossResult, setTossResult] = useState(null); // 'heads' or 'tails' or 'skipped'
  const [selectedTeamForHeads, setSelectedTeamForHeads] = useState(null);
  const [tossWinner, setTossWinner] = useState(null);
  const [winnerChoice, setWinnerChoice] = useState(null);
  const [tossSkipped, setTossSkipped] = useState(false);
  const spinIntervalRef = useRef(null);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setStage('spinning');
      setIsSpinning(true);
      setTossResult(null);
      setSelectedTeamForHeads(null);
      setTossWinner(null);
      setWinnerChoice(null);
      setTossSkipped(false);
    }
  }, [isOpen]);

  // Coin spinning animation
  useEffect(() => {
    if (isSpinning) {
      spinIntervalRef.current = setInterval(() => {
        setCoinFace(prev => prev === 'heads' ? 'tails' : 'heads');
      }, 150);
    } else {
      clearInterval(spinIntervalRef.current);
    }

    return () => clearInterval(spinIntervalRef.current);
  }, [isSpinning]);

  const handleStopSpin = () => {
    setIsSpinning(false);
    // Random result
    const result = Math.random() < 0.5 ? 'heads' : 'tails';
    setTossResult(result);
    setCoinFace(result);
    setStage('result');
  };

  const handleSelectTeamForHeads = (team) => {
    setSelectedTeamForHeads(team);
  };

  const handleConfirmHeadsSelection = () => {
    // Determine winner based on toss result and heads selection
    const winner = tossResult === 'heads' ? selectedTeamForHeads :
                   (selectedTeamForHeads.id === teamA.id ? teamB : teamA);
    setTossWinner(winner);
    setStage('choice');
  };

  const handleSkipToss = () => {
    setIsSpinning(false);
    setTossSkipped(true);
    setTossResult('skipped');
    setStage('skip-select');
  };

  const handleSelectWinnerDirectly = (team) => {
    setTossWinner(team);
    setStage('choice');
  };

  const handleWinnerChoice = (choice) => {
    setWinnerChoice(choice);
  };

  const handleComplete = () => {
    onTossComplete({
      winner: tossWinner,
      choice: winnerChoice,
      tossResult: tossResult,
      tossSkipped: tossSkipped
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className={`${theme.card} rounded-2xl w-full max-w-md overflow-hidden shadow-2xl`}>
        {/* Header */}
        <div className={`px-6 py-4 border-b ${theme.border} flex items-center justify-between`}>
          <h2 className={`text-xl font-bold ${theme.text}`}>{title}</h2>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg ${theme.cardHover} transition-colors`}
          >
            <X className={`w-5 h-5 ${theme.textMuted}`} />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {/* Stage: Spinning */}
          {stage === 'spinning' && (
            <div className="text-center">
              <p className={`text-sm ${theme.textMuted} mb-6`}>
                Click the coin or "Stop" button to stop the toss
              </p>

              {/* Coin Animation */}
              <div
                onClick={handleStopSpin}
                className="relative w-32 h-32 mx-auto mb-6 cursor-pointer"
              >
                <div
                  className={`w-full h-full rounded-full flex items-center justify-center text-4xl font-bold shadow-lg transition-all duration-150 ${
                    isSpinning ? 'animate-pulse' : ''
                  } ${
                    coinFace === 'heads'
                      ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-yellow-900'
                      : 'bg-gradient-to-br from-gray-300 to-gray-500 text-gray-800'
                  }`}
                  style={{
                    transform: isSpinning ? `rotateY(${coinFace === 'heads' ? 0 : 180}deg)` : 'none',
                    animation: isSpinning ? 'spin3d 0.3s linear infinite' : 'none'
                  }}
                >
                  {coinFace === 'heads' ? 'H' : 'T'}
                </div>
              </div>

              <style>{`
                @keyframes spin3d {
                  0% { transform: rotateY(0deg) scale(1); }
                  25% { transform: rotateY(90deg) scale(0.9); }
                  50% { transform: rotateY(180deg) scale(1); }
                  75% { transform: rotateY(270deg) scale(0.9); }
                  100% { transform: rotateY(360deg) scale(1); }
                }
              `}</style>

              <div className="flex gap-3 justify-center">
                <button
                  onClick={handleStopSpin}
                  className="px-8 py-3 bg-red-500 text-white rounded-xl font-semibold hover:bg-red-600 transition-colors shadow-lg"
                >
                  Stop
                </button>
                <button
                  onClick={handleSkipToss}
                  className={`px-8 py-3 rounded-xl font-semibold transition-colors ${theme.backgroundSecondary} ${theme.text} hover:opacity-80`}
                >
                  Skip Toss
                </button>
              </div>
            </div>
          )}

          {/* Stage: Result - Select who called heads */}
          {stage === 'result' && (
            <div className="text-center">
              {/* Result Display */}
              <div className="mb-6">
                <div
                  className={`w-24 h-24 mx-auto rounded-full flex items-center justify-center text-3xl font-bold shadow-lg ${
                    tossResult === 'heads'
                      ? 'bg-gradient-to-br from-yellow-400 to-yellow-600 text-yellow-900'
                      : 'bg-gradient-to-br from-gray-300 to-gray-500 text-gray-800'
                  }`}
                >
                  {tossResult === 'heads' ? 'H' : 'T'}
                </div>
                <p className={`text-2xl font-bold ${theme.text} mt-4`}>
                  It's {tossResult === 'heads' ? 'Heads' : 'Tails'}!
                </p>
              </div>

              {/* Select who called heads */}
              <p className={`text-sm ${theme.textMuted} mb-4`}>
                Who called Heads?
              </p>
              <div className="flex gap-3 justify-center mb-6">
                <button
                  onClick={() => handleSelectTeamForHeads(teamA)}
                  className={`px-6 py-3 rounded-xl font-medium transition-all ${
                    selectedTeamForHeads?.id === teamA.id
                      ? 'bg-blue-500 text-white shadow-lg scale-105'
                      : `${theme.backgroundSecondary} ${theme.text} ${theme.cardHover}`
                  }`}
                >
                  {teamA.name}
                </button>
                <button
                  onClick={() => handleSelectTeamForHeads(teamB)}
                  className={`px-6 py-3 rounded-xl font-medium transition-all ${
                    selectedTeamForHeads?.id === teamB.id
                      ? 'bg-blue-500 text-white shadow-lg scale-105'
                      : `${theme.backgroundSecondary} ${theme.text} ${theme.cardHover}`
                  }`}
                >
                  {teamB.name}
                </button>
              </div>

              <button
                onClick={handleConfirmHeadsSelection}
                disabled={!selectedTeamForHeads}
                className={`px-8 py-3 rounded-xl font-semibold transition-colors ${
                  selectedTeamForHeads
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Confirm
              </button>
            </div>
          )}

          {/* Stage: Skip-Select - Directly select toss winner (no actual toss) */}
          {stage === 'skip-select' && (
            <div className="text-center">
              {/* Skip Toss Header */}
              <div className="mb-6">
                <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3 ${theme.backgroundSecondary}`}>
                  <span className="text-3xl">⚡</span>
                </div>
                <p className={`text-xl font-bold ${theme.text}`}>
                  Toss Skipped
                </p>
              </div>

              {/* Direct Team Selection */}
              <p className={`text-sm ${theme.textMuted} mb-4`}>
                Which team won the toss?
              </p>
              <div className="flex gap-3 justify-center mb-6">
                <button
                  onClick={() => handleSelectWinnerDirectly(teamA)}
                  className={`px-6 py-4 rounded-xl font-medium transition-all ${theme.backgroundSecondary} ${theme.text} ${theme.cardHover} hover:scale-105`}
                >
                  {teamA.name}
                </button>
                <button
                  onClick={() => handleSelectWinnerDirectly(teamB)}
                  className={`px-6 py-4 rounded-xl font-medium transition-all ${theme.backgroundSecondary} ${theme.text} ${theme.cardHover} hover:scale-105`}
                >
                  {teamB.name}
                </button>
              </div>
            </div>
          )}

          {/* Stage: Choice - Winner selects their choice */}
          {stage === 'choice' && (
            <div className="text-center">
              {/* Winner Announcement */}
              <div className="mb-6">
                <div className={`w-16 h-16 mx-auto rounded-full flex items-center justify-center mb-3 ${theme.backgroundSecondary}`}>
                  <span className="text-3xl">{tossSkipped ? '⚡' : '🏆'}</span>
                </div>
                <p className={`text-xl font-bold ${theme.text}`}>
                  {tossSkipped
                    ? `${tossWinner?.name} to decide`
                    : `${tossWinner?.name} wins the toss!`}
                </p>
              </div>

              {/* Choice Selection */}
              <p className={`text-sm ${theme.textMuted} mb-4`}>
                What do they choose?
              </p>
              <div className="flex flex-col gap-3 mb-6">
                {choices.map((choice, index) => (
                  <button
                    key={index}
                    onClick={() => handleWinnerChoice(choice)}
                    className={`px-6 py-4 rounded-xl font-medium transition-all ${
                      winnerChoice === choice
                        ? 'bg-blue-500 text-white shadow-lg'
                        : `${theme.backgroundSecondary} ${theme.text} ${theme.cardHover}`
                    }`}
                  >
                    {choice}
                  </button>
                ))}
              </div>

              <button
                onClick={handleComplete}
                disabled={!winnerChoice}
                className={`px-8 py-3 rounded-xl font-semibold transition-colors ${
                  winnerChoice
                    ? 'bg-green-500 text-white hover:bg-green-600'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                Start Match
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CoinToss;
